import React, { useCallback, useContext, useMemo, useState } from 'react';
import { LoggedInUser, ParentProfile, Profile } from '../../../common/types';
import { useAppFeaturesContext } from '../AppFeaturesContext/AppFeaturesContext';
import { v4 as uuid } from 'uuid';
import {
  acceptTermOfService as acceptTermOfServiceAPI,
  createDeviceToken as createDeviceTokenAPI,
  deviceTokenLogin as deviceTokenLoginAPI,
  DeviceTokenResponse,
  emailLogin as emailLoginAPI,
  fetchLoggedInUser as fetchLoggedInUserAPI,
} from '../../../service/login-api';
import {
  LoginData,
  LoginResponse,
  PollerResponse,
  PollerResponseStatus,
} from '../../appTypings';
import { ErrorName, LoginApiError5xx } from '../../error/errors';
import {
  changeUserProfile,
  fetchProfileCookie as fetchProfileCookies,
} from '../../../service/user-profile-api';
import { isSubProfile } from '../../typeGuards';
import { useReloadAreena } from '../../Routes/reload';
import { getTranslation } from '../../localizations/translations';
import { loginFetchClient } from '../../../service/loginFetchClient';
import { useIdToken } from './useIdToken';
import { clearUserData, newSession } from '@snowplow/browser-tracker';

export type CreateDeviceTokenParams = {
  isRegisterSubRoute: boolean;
};

const AuthContextNotInitializedError = 'Auth context not initialized';

const loginSuccess = ({ user, profileCookies }: LoginData): LoginResponse => {
  return {
    success: 'ok',
    data: {
      user,
      profileCookies,
    },
  };
};

const loginError = (message: string): LoginResponse => {
  return {
    error: message,
  };
};

export type Value = {
  fetchUser: () => Promise<LoggedInUser | null>;
  user: LoggedInUser | null;
  setUser: (user: LoggedInUser | null) => void;
  createDeviceToken: (params: CreateDeviceTokenParams) => Promise<void>;
  loginWithDeviceToken: DeviceTokenResponse | null;
  clearDeviceToken: () => void;
  deviceTokenLogin: (sessionId: string) => Promise<PollerResponse>;
  clearSession: () => void;
  emailLogin: (
    username: string,
    password: string
  ) => Promise<LoginResponse | undefined>;
  changeProfile: (
    profile: Profile,
    parentProfile: ParentProfile | undefined
  ) => Promise<void>;
  acceptTermsOfService: (
    loginParameters: null | {
      username: string;
      password: string;
    }
  ) => Promise<void>;
};

const defaultContext: Value = {
  fetchUser: () => Promise.reject(new Error(AuthContextNotInitializedError)),
  user: null,
  setUser: () => {
    throw new Error(AuthContextNotInitializedError);
  },
  createDeviceToken: () =>
    Promise.reject(new Error(AuthContextNotInitializedError)),
  loginWithDeviceToken: null,
  clearDeviceToken: () => {
    throw new Error(AuthContextNotInitializedError);
  },
  deviceTokenLogin: () =>
    Promise.reject(new Error(AuthContextNotInitializedError)),
  clearSession: () => {
    throw new Error(AuthContextNotInitializedError);
  },
  emailLogin: () => Promise.reject(new Error(AuthContextNotInitializedError)),
  changeProfile: () =>
    Promise.reject(new Error(AuthContextNotInitializedError)),
  acceptTermsOfService: () =>
    Promise.reject(new Error(AuthContextNotInitializedError)),
};

const Context = React.createContext<Value>(defaultContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<LoggedInUser | null>(null);
  const [loginWithDeviceToken, setLoginWithDeviceToken] =
    useState<DeviceTokenResponse | null>(null);
  const { device, languageCode } = useAppFeaturesContext();

  const fetchUser = useCallback(async () => {
    const fetchedUser = await fetchLoggedInUserAPI(loginFetchClient());
    setUser(fetchedUser);
    return fetchedUser;
  }, []);

  const createDeviceToken = useCallback(
    async ({ isRegisterSubRoute }: CreateDeviceTokenParams) => {
      const deviceId = uuid();
      const deviceName =
        `${device?.vendor || ''} ${device?.model || 'TV'}`.trim();
      const deviceToken = await createDeviceTokenAPI(
        loginFetchClient(),
        deviceId,
        deviceName,
        isRegisterSubRoute,
        languageCode
      );
      setLoginWithDeviceToken(deviceToken);
    },
    [device, languageCode]
  );

  const clearDeviceToken = useCallback(() => {
    setLoginWithDeviceToken(null);
  }, []);

  const deviceTokenLogin = useCallback(
    async (sessionId: string): Promise<PollerResponse> => {
      const response = await deviceTokenLoginAPI(loginFetchClient(), sessionId);
      if (response.status >= 500) {
        throw new LoginApiError5xx();
      }

      switch (response.status) {
        case 200: {
          const profileCookies = await fetchProfileCookies(loginFetchClient());
          const user = await fetchUser();

          if (user && profileCookies) {
            return {
              status: PollerResponseStatus.Connected,
              data: {
                user,
                profileCookies,
              },
            };
          }
        }
        case 401:
        default: {
          return { status: PollerResponseStatus.NotConnected };
        }
      }
    },
    [fetchUser]
  );

  const clearSession = useCallback(() => {
    setLoginWithDeviceToken(null);
    setUser(null);
  }, []);

  const emailLogin = useCallback(
    async (username: string, password: string) => {
      try {
        const response = await emailLoginAPI(
          loginFetchClient(),
          username,
          password
        );
        if (response.status >= 500) {
          const error = new LoginApiError5xx();
          return Promise.reject(error);
        }

        switch (response.status) {
          case 200: {
            const profileCookies =
              await fetchProfileCookies(loginFetchClient());
            const user = await response.json();

            if (user && profileCookies) {
              return loginSuccess({
                user,
                profileCookies,
              });
            }
            break;
          }
          case 400:
          case 401: {
            const responseBody = await response.json();
            if (responseBody.errorCode === 'login_failed_activation_required') {
              return loginError(
                getTranslation(
                  'loginErrors_LoginActivationRequiredError',
                  languageCode
                )
              );
            }
            return loginError(
              getTranslation(
                'loginErrors_InvalidLoginCredentials',
                languageCode
              )
            );
          }
          case 409: {
            return loginError(ErrorName.CONFLICT_ERROR);
          }
          default: {
            return Promise.reject(
              new Error(
                loginError(
                  getTranslation('loginErrors_GenericLoginError', languageCode)
                ).error
              )
            );
          }
        }
      } catch (err) {
        if (err instanceof Error) {
          return Promise.reject(err);
        } else {
          return Promise.reject(
            new Error(
              loginError(
                getTranslation('loginErrors_GenericLoginError', languageCode)
              ).error
            )
          );
        }
      }
    },
    [languageCode]
  );

  const reloadAreena = useReloadAreena();

  const changeProfile = useCallback(
    async (profile: Profile, parentProfile: ParentProfile | undefined) => {
      let profileCookie = profile.cookie;
      let subAccountId = null;

      if (!profileCookie && parentProfile && isSubProfile(profile)) {
        profileCookie = parentProfile.cookie;
        subAccountId = profile.userId;
      }

      if (profileCookie) {
        const newUser = await changeUserProfile(
          loginFetchClient(),
          profileCookie,
          subAccountId
        );

        clearUserData();
        newSession();
        setUser(newUser);
        reloadAreena(newUser);
      }
    },
    [reloadAreena]
  );

  const acceptTermsOfService = useCallback(
    async (loginParameters: null | { username: string; password: string }) => {
      const acceptTosWithEmailLogin = async (loginParameters: {
        username: string;
        password: string;
      }) => {
        const acceptedTosVersion = 'LATEST';

        const response = await emailLoginAPI(
          loginFetchClient(),
          loginParameters.username,
          loginParameters.password,
          acceptedTosVersion
        );

        if (response.status >= 500) {
          throw new LoginApiError5xx();
        }

        if (response.status === 200) {
          await fetchUser();
        }
      };

      const acceptTos = async () => {
        const response = await acceptTermOfServiceAPI(loginFetchClient());

        if (response.status === 200) {
          await fetchUser();
        }
      };

      if (loginParameters) {
        await acceptTosWithEmailLogin(loginParameters);
      } else {
        await acceptTos();
      }
    },
    [fetchUser]
  );

  useIdToken(user);

  const value = useMemo<Value>(
    () => ({
      fetchUser,
      user,
      createDeviceToken,
      loginWithDeviceToken,
      clearDeviceToken,
      deviceTokenLogin,
      clearSession,
      emailLogin,
      changeProfile,
      acceptTermsOfService,
      setUser,
    }),
    [
      fetchUser,
      user,
      createDeviceToken,
      loginWithDeviceToken,
      clearDeviceToken,
      deviceTokenLogin,
      clearSession,
      emailLogin,
      changeProfile,
      acceptTermsOfService,
    ]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useAuthContext = () => {
  return useContext(Context);
};
